// Lightweight markdown → HTML renderer used by the in-app manuals.
// Handles the subset actually used: h1/h2/h3, bullet lists, numbered lists,
// bold (**), code spans (backticks), blockquotes, links, and paragraphs.
// Escapes HTML first to avoid injection.
import { API, TOKEN_KEY } from "./api";

export function renderMarkdown(md) {
    if (!md) return "";
    const escape = (s) => s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const lines = escape(md).split(/\r?\n/);
    const out = [];
    let inUl = false, inOl = false, inPara = false;
    const closeLists = () => {
        if (inUl) { out.push("</ul>"); inUl = false; }
        if (inOl) { out.push("</ol>"); inOl = false; }
    };
    const closePara = () => {
        if (inPara) { out.push("</p>"); inPara = false; }
    };
    const inline = (s) => s
        .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-800 text-[13px] text-brand-orange">$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-50 font-semibold">$1</strong>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-brand-orange hover:underline">$1</a>');
    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { closePara(); closeLists(); continue; }
        if (/^#\s+/.test(line)) {
            closePara(); closeLists();
            out.push(`<h1 class="font-display text-3xl font-extrabold mt-8 mb-4 text-brand-orange">${inline(line.replace(/^#\s+/, ""))}</h1>`);
            continue;
        }
        if (/^##\s+/.test(line)) {
            closePara(); closeLists();
            out.push(`<h2 class="font-display text-2xl font-bold mt-6 mb-3 text-brand-orange">${inline(line.replace(/^##\s+/, ""))}</h2>`);
            continue;
        }
        if (/^###\s+/.test(line)) {
            closePara(); closeLists();
            out.push(`<h3 class="font-display text-lg font-bold mt-5 mb-2 text-slate-200">${inline(line.replace(/^###\s+/, ""))}</h3>`);
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            closePara();
            if (inOl) { out.push("</ol>"); inOl = false; }
            if (!inUl) { out.push('<ul class="list-disc pl-6 space-y-1 my-3 text-slate-300">'); inUl = true; }
            out.push(`<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`);
            continue;
        }
        if (/^\d+\.\s+/.test(line)) {
            closePara();
            if (inUl) { out.push("</ul>"); inUl = false; }
            if (!inOl) { out.push('<ol class="list-decimal pl-6 space-y-1 my-3 text-slate-300">'); inOl = true; }
            out.push(`<li>${inline(line.replace(/^\d+\.\s+/, ""))}</li>`);
            continue;
        }
        if (/^>\s+/.test(line)) {
            closePara(); closeLists();
            out.push(`<blockquote class="border-l-2 border-brand-orange/60 pl-4 py-1 my-3 text-slate-400 italic">${inline(line.replace(/^>\s+/, ""))}</blockquote>`);
            continue;
        }
        closeLists();
        if (!inPara) { out.push('<p class="my-3 leading-relaxed text-slate-300">'); inPara = true; }
        else out.push(" ");
        out.push(inline(line));
    }
    closePara(); closeLists();
    return out.join("\n");
}

export async function fetchManualText(filename) {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API}/manuals/${encodeURIComponent(filename)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
}

export async function downloadManual(filename) {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API}/manuals/${encodeURIComponent(filename)}?download=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
